import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectParsedConfigurationItems,
  selectDependencyByType,
} from '../../../../reducers/documentation/documentationReducer';
import { ConfigurationItemMap } from '../../configuration-item-tables/shared/helpers';
import { ConfigurationType } from '../types';
import { checkIfActive } from '../../helpers';
import { selectDocumentationConfigurationItems } from '../../../../reducers/global/parserReducer';
import { ConfigurationItem } from '../../../parser/ParserTypes';

interface UseGetParsedDependencies {
  metadataId: string;
  crmOrgId: string;
  type: 'dependsOnIdsForRules' | 'usedByIdsForRules' | 'usedByIdsForFields';
  onlyActive: boolean;
}

export const useGetParsedDependencies = ({
  crmOrgId,
  metadataId,
  type,
  onlyActive,
}: UseGetParsedDependencies) => {
  const configs = useSelector(selectParsedConfigurationItems(crmOrgId));
  const globalConfigurationItems = useSelector(selectDocumentationConfigurationItems(crmOrgId));

  const rulesIds = useSelector(selectDependencyByType(type))?.[crmOrgId]?.[metadataId];

  return useMemo(() => {
    if (!rulesIds) {
      return {} as ConfigurationItemMap;
    }

    const newDep = {} as ConfigurationItemMap;

    // Create lookup maps to avoid repeated findIndex calls
    const rulesLookup = new Map<string, any>();
    globalConfigurationItems?.forEach((rule) => {
      rulesLookup.set(rule.metadataId, rule);
    });

    const configsLookup = new Map<string, any>();
    configs?.forEach((config) => {
      configsLookup.set(config.metadataId, config);
    });

    Object.keys(rulesIds).forEach((key) => {
      const configKey = key as ConfigurationType;
      const items = rulesIds[configKey];
      const newConfigItems: ConfigurationItem[] = [];

      items.forEach((metadataId) => {
        const rule = rulesLookup.get(metadataId);
        if (rule) {
          if (checkIfActive(onlyActive, rule)) {
            newConfigItems.push(rule);
          }
          return;
        }

        const config = configsLookup.get(metadataId);
        if (config) {
          if (checkIfActive(onlyActive, config)) {
            newConfigItems.push(config);
          }
        }
        return;
      });

      newDep[configKey] = newConfigItems;
    });

    return newDep;
  }, [configs, globalConfigurationItems, rulesIds, onlyActive]);
};
